/**
 * Solutions and their agents — the client mirror of the backend registry
 * (`backend/src/daquma/core/solutions.py`, ADR-0048).
 *
 * A Solution is a sidebar group; an agent is an entry in it. Access is
 * grant-only for everyone but the Administrator: a Solution grant reaches every
 * agent in it, an agent grant reaches that one. `/me` returns the agents the
 * caller can use (`agents`) and the Solutions they appear in (`solutions`); the
 * navigation and `RequireAgent` read those, and the backend re-checks every
 * request. Keep this list in step with the backend registry — the keys are the
 * contract.
 */

export interface AgentInfo {
  key: string;
  solution: string;
  name: string;
}

export interface SolutionInfo {
  key: string;
  name: string;
  agents: AgentInfo[];
}

function solution(key: string, name: string, agents: [string, string][]): SolutionInfo {
  return {
    key,
    name,
    agents: agents.map(([agentKey, agentName]) => ({
      key: agentKey,
      solution: key,
      name: agentName,
    })),
  };
}

/** Order is the order the navigation and the grant panels show them. */
export const SOLUTIONS: SolutionInfo[] = [
  solution("base", "Base", [
    ["explore", "Explore"],
    ["create", "Create"],
    ["review", "Review"],
    ["quality", "Quality"],
  ]),
  solution("validation", "Validation", [
    ["first_line_support", "1st line support"],
    ["second_line_support", "2nd line support"],
    ["release_validation", "Release validation"],
    ["my_tests", "My tests"],
  ]),
  solution("clinical", "Clinical", [
    ["etmf_intake", "eTMF intake"],
    ["etmf_completeness", "eTMF completeness"],
    ["etmf_assignments", "Assignments"],
  ]),
  solution("archive", "Archive", [
    ["archive_search", "Search & Retrieve"],
    ["archive_containers", "Archives"],
  ]),
];

export const AGENTS: Record<string, AgentInfo> = Object.fromEntries(
  SOLUTIONS.flatMap((s) => s.agents.map((a) => [a.key, a])),
);

export function agentName(key: string): string {
  return AGENTS[key]?.name ?? key;
}

/** The Solutions this deployment offers (ADR-0050), in registry order. `keys`
 *  is `/me`'s `deployment_solutions`; absent means every Solution. */
export function deployedSolutions(keys?: readonly string[] | null): SolutionInfo[] {
  if (!keys) return SOLUTIONS;
  const offered = new Set(keys);
  return SOLUTIONS.filter((s) => offered.has(s.key));
}

/** Is this agent part of a Solution the deployment offers? */
export function agentOffered(agentKey: string, keys?: readonly string[] | null): boolean {
  const solution = AGENTS[agentKey]?.solution;
  return solution !== undefined && (!keys || keys.includes(solution));
}

export function solutionName(key: string): string {
  return SOLUTIONS.find((s) => s.key === key)?.name ?? key;
}
